/**
 * Spec 035 v0.4: the company of agents — one tree of units. How a unit reads to a person: its
 * kind, its head and who runs, its place in the tree. No React here: the screens and the tests
 * share it.
 */

import type {
  BudgetOut,
  DepartmentOut,
  LaunchMode,
  LaunchOut,
  LaunchResult,
  MessageKind,
  OrderOut,
  OrderState,
  UnitKind,
  UnitMemberOut,
  UnitRunState,
  UnitDecisionOut,
  UnitSubscriptionOut,
} from "@/api/units";

/** A label only: no rule depends on the kind of a unit. */
export const KIND_LABEL: Record<UnitKind, string> = {
  division: "Департамент",
  department: "Отдел",
  section: "Сектор",
  group: "Группа",
};

export const KINDS: UnitKind[] = ["division", "department", "section", "group"];

/** Deeper trees stop being readable (the server refuses with `unit_too_deep`). */
export const MAX_DEPTH = 5;

export const RUN_LABEL: Record<UnitRunState, string> = {
  active: "работает",
  paused: "на паузе",
  stopped: "остановлен",
  never: "не запускался",
};

export function runTone(state: UnitRunState): "done" | "waiting" | "blocked" | "review" {
  if (state === "active") return "done";
  if (state === "paused") return "review";
  return "waiting";
}

/** «Руководитель: Юрист · работает», or why the unit takes no work yet. */
export function headLine(unit: Pick<DepartmentOut, "head">): string {
  if (!unit.head) return "нет руководителя — поручений не принимает";
  const run = unit.head.waits_launch ? "ждёт запуска" : RUN_LABEL[unit.head.run_state];
  return `Руководитель: ${unit.head.name} · ${run}`;
}

/** The staff without the head, in their order. */
export function staffOf(unit: Pick<DepartmentOut, "members">): UnitMemberOut[] {
  return unit.members.filter((m) => m.role !== "leader");
}

function plural(count: number, one: string, few: string, many: string): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  return mod10 === 1 && mod100 !== 11 ? one : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? few : many;
}

/** «3 сотрудника», «нет сотрудников». */
export function staffLine(count: number): string {
  if (count === 0) return "нет сотрудников";
  return `${count} ${plural(count, "сотрудник", "сотрудника", "сотрудников")}`;
}

/** Spec 042: «2 помощника»; empty when there are none. */
export function helpersLine(count: number): string {
  if (!count) return "";
  return `${count} ${plural(count, "помощник", "помощника", "помощников")}`;
}

export interface TreeNode {
  unit: DepartmentOut;
  children: TreeNode[];
}

/**
 * The units as a tree, top units first, each level by name. A unit whose parent is not in the
 * list (archived, or not loaded) is shown on top, so nothing is lost from sight.
 */
export function buildTree(units: DepartmentOut[]): TreeNode[] {
  const byId = new Map(units.map((u) => [u.id, u]));
  const nodes = new Map<string, TreeNode>(units.map((u) => [u.id, { unit: u, children: [] }]));
  const roots: TreeNode[] = [];
  for (const unit of units) {
    const node = nodes.get(unit.id)!;
    const parent = unit.parent_id ? nodes.get(unit.parent_id) : undefined;
    if (parent && byId.has(unit.parent_id!)) parent.children.push(node);
    else roots.push(node);
  }
  const byName = (a: TreeNode, b: TreeNode) => a.unit.name.localeCompare(b.unit.name, "ru");
  const sortDeep = (list: TreeNode[]) => {
    list.sort(byName);
    for (const n of list) sortDeep(n.children);
  };
  sortDeep(roots);
  return roots;
}

/** Every unit inside `id`, deep: a unit cannot be moved into any of them. */
export function descendantsOf(units: DepartmentOut[], id: string): Set<string> {
  const children = new Map<string, string[]>();
  for (const u of units) {
    if (!u.parent_id) continue;
    children.set(u.parent_id, [...(children.get(u.parent_id) ?? []), u.id]);
  }
  const found = new Set<string>();
  const stack = [...(children.get(id) ?? [])];
  while (stack.length) {
    const next = stack.pop()!;
    if (found.has(next)) continue;
    found.add(next);
    stack.push(...(children.get(next) ?? []));
  }
  return found;
}

/** Levels of the subtree of `id`, itself included. */
export function heightOf(units: DepartmentOut[], id: string): number {
  const children = new Map<string, string[]>();
  for (const u of units) {
    if (!u.parent_id) continue;
    children.set(u.parent_id, [...(children.get(u.parent_id) ?? []), u.id]);
  }
  const down = (node: string, seen: Set<string>): number => {
    const below = (children.get(node) ?? []).filter((c) => !seen.has(c));
    return 1 + Math.max(0, ...below.map((c) => down(c, new Set([...seen, c]))));
  };
  return down(id, new Set([id]));
}

/**
 * Where a unit may go: live units that are neither itself nor inside it, and leave the tree at
 * most MAX_DEPTH deep. `id` null: a new unit (one level of its own).
 */
export function parentChoices(units: DepartmentOut[], id: string | null): DepartmentOut[] {
  const inside = id ? descendantsOf(units, id) : new Set<string>();
  const height = id ? heightOf(units, id) : 1;
  return units
    .filter((u) => !u.archived_at && u.id !== id && !inside.has(u.id) && u.depth + height <= MAX_DEPTH)
    .sort((a, b) => a.path.join("/").localeCompare(b.path.join("/"), "ru"));
}

/** «Маркетинг / Спрос» — where a unit sits. */
export function pathLabel(unit: Pick<DepartmentOut, "path">): string {
  return unit.path.join(" / ");
}

/** The unit an agent works in, and whether it heads it. Spec 042: a helper sits in its staff
 * member's unit — `helperOf` names that staff member. */
export function placeOf(
  units: DepartmentOut[],
  agentId: string,
): { unit: DepartmentOut; head: boolean; helperOf?: UnitMemberOut } | null {
  for (const unit of units) {
    const member = unit.members.find((m) => m.agent_id === agentId);
    if (member) return { unit, head: member.role === "leader" };
    const owner = unit.members.find((m) => (m.helpers ?? []).some((h) => h.agent_id === agentId));
    if (owner) return { unit, head: false, helperOf: owner };
  }
  return null;
}

// ── Step 2: orders ───────────────────────────────────────────────────────────

export const ORDER_STATE_LABEL: Record<OrderState, string> = {
  accepted: "принято",
  working: "в работе",
  input_required: "нужны данные",
  done: "выполнено",
  rejected: "отклонено",
  cancelled: "отменено",
};

export const MESSAGE_KIND_LABEL: Record<MessageKind, string> = {
  message: "",
  question: "вопрос",
  answer: "ответ",
  report: "отчёт",
};

/** What an order's state says at a glance — «ждёт запуска» and «ждёт другую часть» first. */
export function orderStatus(order: Pick<OrderOut, "state" | "waiting" | "waits_launch" | "waits_for_number">): {
  label: string;
  tone: "done" | "waiting" | "blocked" | "review" | "needs";
} {
  if (order.state === "done") return { label: ORDER_STATE_LABEL.done, tone: "done" };
  if (order.state === "rejected" || order.state === "cancelled") return { label: ORDER_STATE_LABEL[order.state], tone: "blocked" };
  if (order.waiting) return { label: `ждёт №${order.waits_for_number}`, tone: "waiting" };
  if (order.waits_launch) return { label: "ждёт запуска", tone: "needs" };
  if (order.state === "input_required") return { label: ORDER_STATE_LABEL.input_required, tone: "needs" };
  return { label: ORDER_STATE_LABEL[order.state], tone: "review" };
}

export interface BoardColumn {
  key: "open" | "input" | "done" | "closed";
  title: string;
  orders: OrderOut[];
}

/** A unit's board: in work, needs data, done, closed — urgent ones first, then by number. */
export function boardOf(orders: OrderOut[]): BoardColumn[] {
  const columns: BoardColumn[] = [
    { key: "open", title: "В работе", orders: [] },
    { key: "input", title: "Нужны данные", orders: [] },
    { key: "done", title: "Выполнено", orders: [] },
    { key: "closed", title: "Отклонено и отменено", orders: [] },
  ];
  const at = { accepted: 0, working: 0, input_required: 1, done: 2, rejected: 3, cancelled: 3 } as const;
  for (const order of orders) columns[at[order.state]].orders.push(order);
  for (const column of columns) {
    column.orders.sort((a, b) => Number(b.urgent) - Number(a.urgent) || a.number - b.number);
  }
  return columns.filter((c) => c.orders.length > 0);
}

/** «Поручение от Кости», «от Маркетинг-руководителя через услугу texts». */
export function givenLine(order: Pick<OrderOut, "given_by" | "service_key" | "parent_number" | "via_agent">): string {
  const who = order.given_by.kind === "person" ? order.given_by.name + (order.via_agent ? " (через агента)" : "") : order.given_by.name;
  const how = order.service_key ? ` · услуга «${order.service_key}»` : order.parent_number ? ` · часть №${order.parent_number}` : "";
  return `от ${who}${how}`;
}

// ── Step 3: launch modes ─────────────────────────────────────────────────────

export const LAUNCH_MODE_LABEL: Record<LaunchMode, string> = {
  manual: "Вручную",
  auto: "Сам, когда есть работа",
  schedule: "По расписанию",
};

/** «По расписанию 09:00, 18:30 · ПК DESKTOP (не на связи)». */
export function launchLine(launch: Pick<LaunchOut, "mode" | "times" | "device">): string {
  const mode = launch.mode === "schedule" ? `${LAUNCH_MODE_LABEL.schedule} ${launch.times.join(", ")}` : LAUNCH_MODE_LABEL[launch.mode];
  if (launch.mode === "manual") return `${mode}: работа ждёт запуска`;
  if (!launch.device || launch.device.revoked) return `${mode} · ПК не выбран — пока как вручную`;
  return `${mode} · ПК ${launch.device.name}${launch.device.online ? "" : " (не на связи — пока как вручную)"}`;
}

/** «9:00, 18:30» → ["09:00", "18:30"]; null when a time is not a time. */
export function parseTimes(text: string): string[] | null {
  const parts = text.split(/[,\s;]+/).filter(Boolean);
  const out = new Set<string>();
  for (const part of parts) {
    const match = /^([01]?\d|2[0-3])[:.]([0-5]\d)$/.exec(part);
    if (!match) return null;
    out.add(`${match[1].padStart(2, "0")}:${match[2]}`);
  }
  return [...out].sort();
}

const RESULT_WORD: Record<LaunchResult["result"], string> = {
  launched: "запускается",
  running: "уже работает",
  offline: "ПК не на связи",
  refused: "не запущен",
};

/** «Юрист — запускается; Тексты — не запущен: версия 3 не одобрена». */
export function launchResultsLine(results: LaunchResult[]): string {
  if (!results.length) return "Некого запускать: у подразделения нет руководителя.";
  return results
    .map((r) => `${r.name} — ${RESULT_WORD[r.result]}${r.message && r.result === "refused" ? `: ${r.message}` : ""}`)
    .join("; ");
}

// ── Step 4: events and subscriptions ─────────────────────────────────────────

/** «Маркетинг: case-accepted», «Маркетинг: все события». */
export function listensLine(sub: Pick<UnitSubscriptionOut, "source_unit_name" | "kind">): string {
  return `${sub.source_unit_name}: ${sub.kind ?? "все события"}`;
}

/** «Контент слушает case-accepted». */
export function listenerLine(sub: Pick<UnitSubscriptionOut, "unit_name" | "kind">): string {
  return `${sub.unit_name} слушает ${sub.kind ?? "все события"}`;
}

// ── Step 5: decisions for people ─────────────────────────────────────────────

/** «Костя: Да, публикуй», «Костя через агента: …», «снят». */
export function decisionLine(d: Pick<UnitDecisionOut, "state" | "answer" | "decided_by" | "via_agent">): string {
  if (d.state === "cancelled") return "снят";
  if (d.state === "open") return "ждёт ответа";
  const who = d.decided_by ? d.decided_by.name + (d.via_agent ? " через агента" : "") : "Ответ";
  return `${who}: ${d.answer ?? ""}`;
}

// ── Step 6: budgets ──────────────────────────────────────────────────────────

/** «1 250 000» — tokens read at a glance. */
export function tokens(n: number): string {
  return Math.round(n).toLocaleString("ru-RU").replace(/\s/g, " ");
}

/** «потрачено 1 100 из 2 000 токенов», «без лимита: потрачено 300». */
export function budgetLine(b: Pick<BudgetOut, "tokens_per_month" | "added" | "spent" | "over">): string {
  if (b.tokens_per_month === null) return `без лимита · потрачено ${tokens(b.spent)}`;
  const cap = b.tokens_per_month + b.added;
  return `${b.over ? "лимит исчерпан · " : ""}потрачено ${tokens(b.spent)} из ${tokens(cap)}`;
}

/** «1,5 млн» → 1500000; «200000» → 200000; null when it is not a number. */
export function parseTokens(text: string): number | null {
  const t = text.trim().toLowerCase().replace(/\s/g, "").replace(",", ".");
  const m = /^(\d+(?:\.\d+)?)(млн|тыс|к|m|k)?$/.exec(t);
  if (!m) return null;
  const mult = m[2] === "млн" || m[2] === "m" ? 1_000_000 : m[2] ? 1_000 : 1;
  const n = Math.round(parseFloat(m[1]) * mult);
  return n > 0 ? n : null;
}
